import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import styled from "@emotion/styled";
import { Link } from "react-router-dom";
import * as Yup from "yup";
import { Formik } from "formik";

import {
  Alert as MuiAlert,
  Checkbox,
  FormControlLabel,
  Button,
  TextField as MuiTextField,
  IconButton,
  InputAdornment,
  Box,
  Typography,
} from "@mui/material";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import { spacing } from "@mui/system";

import useAuth from "../../hooks/useAuth";

const Alert = styled(MuiAlert)(spacing);

const TextField = styled(MuiTextField)(spacing);

function SignIn({ isGlassMode, isNewDesign }) {
  const navigate = useNavigate();
  const { signIn, user } = useAuth();
  const [showPassword, setShowPassword] = useState(false);

  useEffect(() => {
    if (user != null) {
      navigate("/dashboard/default");
    }
  }, [user]);

  const togglePasswordVisibility = () => {
    setShowPassword((prev) => !prev);
  };

  const glassInputStyles = {
    "& .MuiOutlinedInput-root": {
      backgroundColor: "rgba(255, 255, 255, 0.05)",
      borderRadius: "12px",
      color: "white",
      "& fieldset": {
        borderColor: "rgba(255, 255, 255, 0.2)",
      },
      "&:hover fieldset": {
        borderColor: "rgba(255, 255, 255, 0.4)",
      },
      "&.Mui-focused fieldset": {
        borderColor: "#22d3ee",
      },
    },
    "& .MuiInputLabel-root": {
      color: "rgba(255, 255, 255, 0.5)",
    },
    "& .MuiInputLabel-root.Mui-focused": {
      color: "#22d3ee",
    },
  };

  const newDesignInputStyles = {
    "& .MuiOutlinedInput-root": {
      backgroundColor: "#ffffff",
      borderRadius: "8px",
      color: "#000000",
      height: "50px",
      "& fieldset": {
        borderColor: "rgba(0, 0, 0, 0.2)",
      },
      "&:hover fieldset": {
        borderColor: "rgba(0, 0, 0, 0.4)",
      },
      "&.Mui-focused fieldset": {
        borderColor: "#3b82f6",
      },
    },
    "& .MuiInputAdornment-root .MuiIconButton-root": {
      color: "rgba(0, 0, 0, 0.5)",
    },
    "& .MuiInputLabel-root": {
      color: "rgba(0, 0, 0, 0.5)",
      transform: "translate(14px, 14px) scale(1)",
    },
    "& .MuiInputLabel-root.Mui-focused, & .MuiInputLabel-root.MuiFormLabel-filled": {
      color: "#3b82f6",
      transform: "translate(14px, -9px) scale(0.75)",
      backgroundColor: "#ffffff",
      padding: "0 4px",
    },
    "& input::placeholder": {
      color: "rgba(0, 0, 0, 0.3)",
    },
    "& input:-webkit-autofill, & input:-webkit-autofill:hover, & input:-webkit-autofill:focus, & input:-webkit-autofill:active":
      {
        WebkitBoxShadow: "0 0 0 1000px #ffffff inset !important",
        WebkitTextFillColor: "#000000 !important",
        transition: "background-color 5000s ease-in-out 0s",
      },
  };

  return (
    <Formik
      initialValues={{
        username: "",
        password: "",
        submit: false,
      }}
      validationSchema={Yup.object().shape({
        username: Yup.string().required("Username is required"),
        password: Yup.string().max(255).required("Password is required"),
      })}
      onSubmit={async (values, { setErrors, setStatus, setSubmitting }) => {
        try {
          await signIn(values.username, values.password);
        } catch (error) {
          const message = error.message || "Something went wrong";

          setStatus({ success: false });
          setErrors({ submit: message });
          setSubmitting(false);
        }
      }}
    >
      {({ errors, handleBlur, handleChange, handleSubmit, isSubmitting, touched, values }) => (
        <form noValidate onSubmit={handleSubmit}>
          {errors.submit && (
            <Alert mt={2} mb={3} severity="warning">
              {errors.submit}
            </Alert>
          )}
          <TextField
            name="username"
            label="Username"
            value={values.username}
            error={Boolean(touched.username && errors.username)}
            fullWidth
            helperText={touched.username && errors.username}
            onBlur={handleBlur}
            onChange={handleChange}
            my={2}
            sx={isNewDesign ? newDesignInputStyles : isGlassMode ? glassInputStyles : {}}
            data-testid="username"
          />
          <TextField
            type={showPassword ? "text" : "password"}
            name="password"
            label="Password"
            value={values.password}
            error={Boolean(touched.password && errors.password)}
            fullWidth
            helperText={touched.password && errors.password}
            onBlur={handleBlur}
            onChange={handleChange}
            my={2}
            sx={isNewDesign ? newDesignInputStyles : isGlassMode ? glassInputStyles : {}}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={togglePasswordVisibility}
                    edge="end"
                    aria-label="toggle password visibility"
                    sx={{
                      color: isNewDesign
                        ? "rgba(0, 0, 0, 0.5)"
                        : isGlassMode
                          ? "rgba(255, 255, 255, 0.6)"
                          : "inherit",
                    }}
                  >
                    {showPassword ? <Visibility /> : <VisibilityOff />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
            data-testid="password"
          />

          {isNewDesign ? (
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mb: 4,
                mt: 1,
              }}
            >
              <FormControlLabel
                control={
                  <Checkbox
                    sx={{
                      color: isNewDesign ? "rgba(0, 0, 0, 0.3)" : "rgba(255, 255, 255, 0.3)",
                      "&.Mui-checked": { color: "#3b82f6" },
                    }}
                  />
                }
                label={
                  <Typography
                    sx={{
                      color: isNewDesign ? "rgba(0, 0, 0, 0.7)" : "rgba(255, 255, 255, 0.7)",
                      fontSize: "0.875rem",
                    }}
                  >
                    Remember me
                  </Typography>
                }
              />
              <Typography
                component={Link}
                to="/auth/reset-password"
                variant="body2"
                sx={{
                  color: isNewDesign ? "#60a5fa" : "rgba(255, 255, 255, 0.7)",
                  textDecoration: "none",
                  fontWeight: isNewDesign ? 500 : 400,
                  "&:hover": { textDecoration: "underline" },
                }}
              >
                Forgot password?
              </Typography>
            </Box>
          ) : null}

          <Button
            type="submit"
            fullWidth
            variant="contained"
            disabled={isSubmitting}
            sx={{
              mt: 2,
              mb: 2,
              py: isNewDesign ? 1.8 : 1.5,
              borderRadius: isNewDesign ? "10px" : "25px",
              fontSize: "1.1rem",
              fontWeight: 600,
              textTransform: "none",
              background: isNewDesign
                ? "linear-gradient(90deg, #3b82f6 0%, #8b5cf6 100%)"
                : isGlassMode
                  ? "linear-gradient(90deg, #0ea5e9 0%, #22d3ee 100%)"
                  : undefined,
              boxShadow: isNewDesign ? "0 4px 15px rgba(59, 130, 246, 0.4)" : undefined,
              "&:hover": {
                background: isNewDesign
                  ? "linear-gradient(90deg, #2563eb 0%, #7c3aed 100%)"
                  : isGlassMode
                    ? "linear-gradient(90deg, #0284c7 0%, #0891b2 100%)"
                    : undefined,
              },
            }}
            data-testid="login-btn"
          >
            Sign In
          </Button>

          {!isNewDesign && (
            <Box sx={{ display: "flex", justifyContent: "center", gap: 2, mt: 1 }}>
              <Typography
                component={Link}
                to="/auth/reset-password"
                variant="body2"
                sx={{
                  color: isGlassMode ? "rgba(255, 255, 255, 0.7)" : "primary.main",
                  textDecoration: "none",
                  "&:hover": { textDecoration: "underline" },
                }}
              >
                Forgot password?
              </Typography>
              <Typography
                component={Link}
                to="/auth/sign-up"
                variant="body2"
                sx={{
                  color: isGlassMode ? "rgba(255, 255, 255, 0.7)" : "primary.main",
                  textDecoration: "none",
                  fontWeight: 600,
                  "&:hover": { textDecoration: "underline" },
                }}
              >
                Sign up
              </Typography>
            </Box>
          )}

          <Typography
            variant="caption"
            align="center"
            display="block"
            sx={{
              mt: 3,
              color: isNewDesign
                ? "rgba(0, 0, 0, 0.45)"
                : isGlassMode
                  ? "rgba(255, 255, 255, 0.35)"
                  : "text.disabled",
              letterSpacing: "0.05em",
            }}
          >
            Powered by Hyperradius
          </Typography>
        </form>
      )}
    </Formik>
  );
}

export default SignIn;
